<template>
  <section class="proyecto-panel">
    <div v-if="error" class="mod-alert-error">
      <i class="bi bi-exclamation-triangle-fill"></i>
      {{ error }}
    </div>

    <div class="proyecto-panel-form">
      <div class="mod-field">
        <label>Archivo <span class="mod-required">*</span></label>
        <input ref="archivoInput" type="file" @change="onArchivo">
      </div>
      <div class="mod-field">
        <label>Tipo de archivo</label>
        <CatSearch
          catalogo="proyecto_archivo_tipo"
          v-model="categoria"
          placeholder="Buscar tipo de archivo..."
          display-field="nombre"
          :min-length="0"
        />
      </div>
      <div class="mod-field mod-field-check">
        <label class="mod-check-label">
          <input v-model="esPrincipal" type="checkbox">
          <span>Principal</span>
        </label>
      </div>
      <div class="mod-field proyecto-panel-wide">
        <label>Observaciones</label>
        <input v-model="observaciones" type="text" placeholder="Notas del archivo">
      </div>
      <div class="proyecto-panel-actions">
        <button type="button" class="mod-btn mod-btn-save" :disabled="saving || !archivo" @click="subir">
          <span v-if="saving" class="mod-spinner-sm"></span>
          <template v-else>
            <i class="bi bi-upload"></i>
            Subir archivo
          </template>
        </button>
      </div>
    </div>

    <div class="proyecto-table-wrap">
      <div v-if="loading" class="proyecto-empty">Cargando archivos...</div>
      <div v-else-if="items.length === 0" class="proyecto-empty">Sin archivos adjuntos</div>
      <table v-else class="proyecto-table">
        <thead>
          <tr>
            <th>Archivo</th>
            <th>Tipo</th>
            <th>Tamaño</th>
            <th>Fecha</th>
            <th>Observaciones</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.ProyectoArchivoId">
            <td>
              <strong>{{ item.NombreOriginal }}</strong>
              <span v-if="item.EsPrincipal" class="proyecto-badge">Principal</span>
            </td>
            <td>{{ item.Categoria || '-' }}</td>
            <td>{{ formatoTamano(item.TamanoBytes) }}</td>
            <td>{{ formatoFecha(item.FechaInserta) }}</td>
            <td>{{ item.Observaciones || '-' }}</td>
            <td class="proyecto-row-actions">
              <button
                v-if="service.esVisualizable(item)"
                type="button"
                class="proyecto-icon-btn"
                title="Ver"
                @click="visualizar(item)"
              >
                <i class="bi bi-eye"></i>
              </button>
              <button type="button" class="proyecto-icon-btn" title="Descargar" @click="descargar(item)">
                <i class="bi bi-download"></i>
              </button>
              <button type="button" class="proyecto-icon-btn danger" title="Eliminar" @click="borrar(item)">
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { showToast } from '@/shared/utils/toast';
import CatSearch from '@/shared/components/CatSearch.vue';
import proyectoGestionService from './proyectoGestion.service';

export default {
  name: 'ProyectoArchivosPanel',
  components: { CatSearch },
  props: {
    proyectoClave: { type: String, required: true }
  },
  data() {
    return {
      service: proyectoGestionService,
      items: [],
      archivo: null,
      categoria: '',
      observaciones: '',
      esPrincipal: false,
      loading: false,
      saving: false,
      error: null
    };
  },
  created() {
    this.cargar();
  },
  methods: {
    async cargar() {
      this.loading = true;
      try {
        this.items = await proyectoGestionService.listarArchivos(this.proyectoClave);
      } catch {
        this.error = 'No fue posible cargar archivos';
      } finally {
        this.loading = false;
      }
    },
    onArchivo(event) {
      this.archivo = event.target.files?.[0] || null;
    },
    limpiar() {
      this.archivo = null;
      this.categoria = '';
      this.observaciones = '';
      this.esPrincipal = false;
      if (this.$refs.archivoInput) this.$refs.archivoInput.value = '';
    },
    async subir() {
      if (!this.archivo) return;
      this.saving = true;
      this.error = null;
      try {
        await proyectoGestionService.subirArchivo(this.archivo, this.proyectoClave, {
          categoria: this.categoria,
          observaciones: this.observaciones,
          esPrincipal: this.esPrincipal
        });
        showToast('Archivo subido correctamente', 'success');
        this.limpiar();
        await this.cargar();
      } catch {
        this.error = 'Error al subir archivo';
      } finally {
        this.saving = false;
      }
    },
    async descargar(item) {
      try {
        await proyectoGestionService.descargarArchivo(item);
      } catch {
        this.error = 'No fue posible descargar el archivo';
      }
    },
    async visualizar(item) {
      try {
        await proyectoGestionService.visualizarArchivo(item);
      } catch {
        this.error = 'No fue posible visualizar el archivo';
      }
    },
    async borrar(item) {
      if (!confirm('Eliminar este archivo del proyecto?')) return;
      try {
        const result = await proyectoGestionService.borrarArchivo(item);
        if (result.Number >= 0) {
          showToast(result.Message || 'Archivo eliminado', result.Type || 'success');
          await this.cargar();
        } else {
          this.error = result.Message || 'Error al eliminar archivo';
        }
      } catch {
        this.error = 'Error de conexion con el servidor';
      }
    },
    formatoTamano(bytes) {
      const n = Number(bytes || 0);
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      return `${(n / 1024 / 1024).toFixed(1)} MB`;
    },
    formatoFecha(fecha) {
      if (!fecha) return '-';
      return new Date(fecha).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";
@import "./proyectoGestionPanels.css";
</style>
